import {
  useCallback,
  useState,
  createContext,
  useContext,
  useMemo,
  ReactNode,
  RefObject,
  Dispatch,
  SetStateAction,
} from 'react';
import type { ReactPlayerProps } from 'react-player/types';

export type PlayerRef = RefObject<HTMLVideoElement | null>;

// unique id for map lookup
// eg: asset uuid, url, or react useId...
export type UniqueId = string;

// Player item type
export type PlayerItem = {
  uid: UniqueId;
  ref: PlayerRef;
} & Omit<ReactPlayerProps, 'ref'>;

export type CosmosPlayerContextType = {
  players: Map<UniqueId, PlayerItem>;
  addPlayer: (player: PlayerItem) => void;
  pauseAllOtherPlayers: (currentId: UniqueId) => void;
  pauseAllPlayers: () => void;
  removePlayer: (uid: UniqueId) => void;
  getPlayer: (uid: UniqueId) => PlayerItem | undefined;
  setPlayerProps: (uid: UniqueId, props: Partial<Omit<ReactPlayerProps, 'ref'>>) => void;
  currentlyPlayingId: UniqueId | null;
  setCurrentlyPlayingId: Dispatch<SetStateAction<UniqueId | null>>;
  pauseAllGeneration: number;
};

// Create the context with a default value
export const CosmosPlayerContext = createContext<CosmosPlayerContextType | undefined>(
  undefined,
);

// Props for context provider
interface CosmosPlayerContextProviderProps {
  children: ReactNode;
}

// Hook for CosmosPlayerContext
export const useCosmosPlayerContext = () => {
  const context = useContext(CosmosPlayerContext);
  if (!context) {
    throw new Error(
      'useCosmosPlayerContext must be used within a CosmosPlayerContextProvider',
    );
  }
  return context;
};

// Empty initial map state
const initState = new Map<UniqueId, PlayerItem>();

// The provider component
export const CosmosPlayerContextProvider = ({
  children,
}: CosmosPlayerContextProviderProps) => {
  // Map of unique IDs to player objects
  const [players, setPlayers] = useState<Map<UniqueId, PlayerItem>>(initState);

  // Track which player is currently playing
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<UniqueId | null>(null);

  // Counter that increments each time pauseAllPlayers is called
  // CosmosPlayer components watch this to self-pause
  const [pauseAllGeneration, setPauseAllGeneration] = useState(0);

  const pauseAllOtherPlayers = useCallback((currentId: UniqueId) => {
    // Setting currentlyPlayingId signals other CosmosPlayer components
    // to pause themselves via their useEffect watcher.
    // We do NOT directly call .pause() on the DOM elements because
    // react-player v3 will detect the mismatch with the `playing` prop
    // and re-sync by calling .play(), causing a feedback loop.
    setCurrentlyPlayingId(currentId);
  }, []);

  // Pause all players.
  const pauseAllPlayers = useCallback(() => {
    // Clear currently playing player
    setCurrentlyPlayingId(null);
    // Increment generation to signal all CosmosPlayer components to pause
    setPauseAllGeneration((prev) => prev + 1);
  }, []);

  // Add a player to state map
  const addPlayer = useCallback((player: PlayerItem) => {
    setPlayers((prev) => {
      // Already registered player no need to update
      if (prev.has(player.uid)) {
        return prev;
      }
      const m = new Map(prev);
      m.set(player.uid, player);
      return m;
    });
  }, []);

  // Remove a player by uid from the state map
  const removePlayer = useCallback((uid: UniqueId) => {
    setPlayers((prev) => {
      // Player not found or was already unregistered
      if (!prev.has(uid)) {
        return prev;
      }
      const m = new Map(prev);
      m.delete(uid);
      return m;
    });

    // Reset currentlyPlayingId if the removed player was playing
    setCurrentlyPlayingId((prevId) => {
      if (prevId === uid) {
        return null;
      }
      return prevId;
    });
  }, []);

  // Upsert new props for an existing player obj
  const setPlayerProps = useCallback(
    (uid: UniqueId, props: Partial<Omit<ReactPlayerProps, 'ref'>>) => {
      setPlayers((prev) => {
        const existing = prev.get(uid);
        // Player not found or was already unregistered
        if (!existing) {
          return prev;
        }
        const m = new Map(prev);
        m.set(uid, { ...existing, ...props });
        return m;
      });
    },
    [],
  );

  // Get a player by its uid from the map
  const getPlayer = useCallback((uid: UniqueId) => players.get(uid), [players]);

  const contextValue = useMemo(
    () => ({
      players,
      addPlayer,
      pauseAllOtherPlayers,
      pauseAllPlayers,
      removePlayer,
      getPlayer,
      setPlayerProps,
      currentlyPlayingId,
      setCurrentlyPlayingId,
      pauseAllGeneration,
    }),
    [
      players,
      addPlayer,
      pauseAllOtherPlayers,
      pauseAllPlayers,
      removePlayer,
      getPlayer,
      setPlayerProps,
      currentlyPlayingId,
      setCurrentlyPlayingId,
      pauseAllGeneration,
    ],
  );

  return (
    <CosmosPlayerContext.Provider value={contextValue}>
      {children}
    </CosmosPlayerContext.Provider>
  );
};
